import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Banner, Button, Card, IconButton, StepList, Tabs, TextField, TopAppBar, useSnackbar,
} from '@meshble/molo';
import { api, must, type ServerCreated } from '../lib/api';
import { Back, Copy } from '../icons';
import { Anello } from '../brand';
import { t } from '../lib/lingua';
import { MACCHINA_VUOTA, ScegliMacchina, type MacchinaCloud } from './server-cloud';

/**
 * Un server nuovo, in due modi.
 *
 * **Collegare** una macchina che esiste già — nata dal pannello del proprio
 * provider, Google Cloud, Azure, on-prem — e che questo pannello adotta. È la
 * stessa scelta che fa CloudPepper per gli stessi provider (01 §47), e regge
 * perché la pipeline degli step non sa niente di cloud: vuole SSH verso una
 * Ubuntu 24.04 e nient'altro.
 *
 * **Crearla** su OVH, dal 18/09/2026: con le chiavi del progetto Public Cloud
 * dell'organizzazione (Impostazioni), l'hub la fa nascere lui, la chiave
 * arriva col cloud-init e il provisioning parte da solo. È il pezzo che rende
 * «Il server è perso» un click senza mani.
 */
const NOME_VALIDO = /^[a-z0-9][a-z0-9-]*$/;

export function NewServerPage() {
  const { slug } = useParams({ from: '/shell/orgs/$slug/servers/new' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const avvisa = useSnackbar();

  const [modo, setModo] = useState<'collega' | 'crea'>('collega');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [port, setPort] = useState('22');
  const [macchina, setMacchina] = useState<MacchinaCloud>(MACCHINA_VUOTA);
  const [creato, setCreato] = useState<ServerCreated | null>(null);

  const crea = useMutation({
    mutationFn: () =>
      must(
        api.POST('/api/v1/orgs/{slug}/servers', {
          params: { path: { slug } },
          body: { name, provider: 'ovh', ...macchina },
        }),
      ),
    onSuccess: (server) => {
      qc.invalidateQueries({ queryKey: ['servers', slug] });
      avvisa(t('Sto creando {nome} su OVH: l’installazione parte da sola', { nome: server.name }));
      navigate({ to: '/orgs/$slug/servers/$id', params: { slug, id: server.id } });
    },
  });

  const collega = useMutation({
    mutationFn: () =>
      must(
        api.POST('/api/v1/orgs/{slug}/servers', {
          params: { path: { slug } },
          body: {
            name,
            provider: 'custom_ssh',
            address: address.trim(),
            // 22 è già il default lato hub: mandarlo comunque non cambia
            // niente, e mandare 0 sarebbe fuori dall'intervallo dichiarato.
            ...(Number(port) !== 22 ? { sshPort: Number(port) } : {}),
          },
        }),
      ),
    onSuccess: (server) => {
      qc.invalidateQueries({ queryKey: ['servers', slug] });
      setCreato(server);
    },
  });

  const nomeOk = NOME_VALIDO.test(name) && name.length >= 2 && name.length <= 63;
  const portaOk = Number.isInteger(Number(port)) && Number(port) >= 1 && Number(port) <= 65535;
  const pronto = nomeOk && portaOk && address.trim().length > 0 && !collega.isPending;
  const prontoCrea = nomeOk && !!macchina.credentialId && !!macchina.region && !!macchina.size && !crea.isPending;

  if (creato) {
    return <BootstrapIstruzioni slug={slug} server={creato} />;
  }

  return (
    <>
      <TopAppBar
        leading={
          <IconButton
            label={t('Torna ai server')}
            onClick={() => navigate({ to: '/orgs/$slug/servers', params: { slug } })}
          >
            <Back />
          </IconButton>
        }
        title={modo === 'crea' ? t('Crea un server su OVH') : t('Collega un server')}
        subtitle={modo === 'crea' ? t('Nel progetto Public Cloud dell’azienda, con Ubuntu 24.04') : t('Una macchina con Ubuntu 24.04 appena installato')}
      />
      <div className="md-content">
        <div className="md-stack">
          <Tabs
            value={modo}
            onValueChange={(v) => setModo(v as 'collega' | 'crea')}
            tabs={[{ value: 'collega', label: t('Collega una macchina che hai già') }, { value: 'crea', label: t('Creala su OVH') }]}
          />

          {/* Prima di chiedere i campi si dice cosa succederà: sono tre passi
              e, collegando, uno solo tocca a chi legge; creando, nessuno. */}
          <Card variant="filled">
            <h2 className="md-title-medium" style={{ margin: 0 }}>{t('Come funziona')}</h2>
            {modo === 'crea' ? (
              <StepList
                items={[
                  { state: 'current', title: t('Scegli progetto, regione e taglia'), detail: t('Qui sotto. Le regioni e le taglie le dice OVH.') },
                  { state: 'pending', title: t('Molo crea la macchina'), detail: t('Con la sua chiave già a bordo: nessun comando da eseguire.') },
                  { state: 'pending', title: t('Molo installa tutto da solo'), detail: t('Database, server web, sicurezza e base di Odoo. Circa dieci minuti.') },
                ]}
              />
            ) : (
              <StepList
                items={[
                  { state: 'current', title: t('Dici a Molo come si chiama e dove si trova'), detail: t('Qui sotto. Ci vuole un minuto.') },
                  { state: 'pending', title: t('Esegui un comando sulla macchina'), detail: t('Te lo diamo pronto da copiare: autorizza Molo a entrare.') },
                  { state: 'pending', title: t('Molo installa tutto da solo'), detail: t('Database, server web, sicurezza e base di Odoo. Circa dieci minuti.') },
                ]}
              />
            )}
          </Card>

          {modo === 'crea' && (
            <Card variant="outlined">
              <form className="md-form" onSubmit={(e) => { e.preventDefault(); if (prontoCrea) crea.mutate(); }}>
                <TextField
                  label={t('Nome')}
                  value={name}
                  autoFocus
                  onChange={(e) => setName(e.target.value.toLowerCase())}
                  error={name.length > 0 && !nomeOk}
                  help={name.length > 0 && !nomeOk
                    ? t('Solo minuscole, cifre e trattini, a partire da una lettera o una cifra.')
                    : t('È anche il nome della macchina su OVH. Non si cambia più.')}
                />
                <ScegliMacchina slug={slug} value={macchina} onChange={setMacchina} />
                {crea.error && <Banner tone="error" title={t('Non sono riuscito a crearlo')}>{crea.error.message}</Banner>}
                <div className="md-form__actions">
                  <Button type="button" variant="text" onClick={() => navigate({ to: '/orgs/$slug/servers', params: { slug } })}>{t('Annulla')}</Button>
                  <Button type="submit" variant="filled" icon={crea.isPending ? <Anello /> : undefined} disabled={!prontoCrea}>
                    {crea.isPending ? t('Creo…') : t('Crea il server')}
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {modo === 'collega' && (
          <Card variant="outlined">
            <form
              className="md-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (pronto) collega.mutate();
              }}
            >
              <TextField
                label={t('Nome')}
                value={name}
                autoFocus
                onChange={(e) => setName(e.target.value.toLowerCase())}
                error={name.length > 0 && !nomeOk}
                help={
                  name.length > 0 && !nomeOk
                    ? t('Solo minuscole, cifre e trattini, a partire da una lettera o una cifra.')
                    : t('Come lo chiami tu, es. «produzione» o «srv-milano». Non si cambia più.')
                }
              />

              <div className="md-form__row">
                <TextField
                  label={t('Indirizzo')}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="203.0.113.24"
                  help={t('L\'IP pubblico o il nome della macchina, come lo vedi dal tuo fornitore.')}
                />
                <TextField
                  label={t('Porta SSH')}
                  value={port}
                  inputMode="numeric"
                  onChange={(e) => setPort(e.target.value)}
                  error={port.length > 0 && !portaOk}
                  help={t('Se non l\'hai cambiata, è 22.')}
                  className="md-form__port"
                />
              </div>

              {collega.error && (
                <Banner tone="error" title={t('Non sono riuscito a collegarlo')}>{collega.error.message}</Banner>
              )}

              <div className="md-form__actions">
                <Button
                  type="button"
                  variant="text"
                  onClick={() => navigate({ to: '/orgs/$slug/servers', params: { slug } })}
                >
                  {t('Annulla')}
                </Button>
                <Button type="submit" variant="filled" icon={collega.isPending ? <Anello /> : undefined} disabled={!pronto}>
                  {collega.isPending ? t('Collego…') : t('Avanti')}
                </Button>
              </div>
            </form>
          </Card>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * L'hub non ha modo di entrare finché qualcuno non gli autorizza una chiave, e
 * quel comando va eseguito a mano sulla macchina. È l'unico passaggio manuale
 * di tutto il flusso.
 */
function BootstrapIstruzioni({ slug, server }: { slug: string; server: ServerCreated }) {
  const navigate = useNavigate();
  const avvisa = useSnackbar();

  const copia = () => {
    void navigator.clipboard.writeText(server.bootstrapCommand ?? '');
    avvisa(t('Comando copiato'));
  };

  return (
    <>
      <TopAppBar title={server.name} subtitle={t('Un ultimo passaggio, sulla macchina')} />
      <div className="md-content">
        <div className="md-stack">
          <Card variant="filled">
            <StepList
              items={[
                { state: 'done', title: t('Nome e indirizzo') },
                { state: 'current', title: t('Esegui il comando sulla macchina'), detail: t('Tocca a te: qui sotto.') },
                { state: 'pending', title: t('Molo installa tutto da solo'), detail: t('Parte appena il comando è stato eseguito.') },
              ]}
            />
          </Card>

          <Card variant="outlined">
            <div className="md-form">
              <p className="md-body-large" style={{ margin: 0 }}>
                {t('Entra come {utente} su {macchina} e incolla questo comando. Autorizza Molo a entrare e nient\'altro: da lì in poi l\'installazione va avanti da sola, e la puoi seguire dalla scheda del server.', {
                  utente: <strong>root</strong>,
                  macchina: <span className="md-mono">{server.ipv4 || server.name}</span>,
                })}
              </p>
              <pre className="md-log md-code">{server.bootstrapCommand}</pre>
              <div className="md-form__actions">
                <Button variant="tonal" icon={<Copy />} onClick={copia}>
                  {t('Copia il comando')}
                </Button>
                <Button variant="filled" onClick={() => navigate({ to: '/orgs/$slug/servers/$id', params: { slug, id: server.id } })}>
                  {t('L\'ho eseguito')}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
